// Add candidates (parked behind a feature flag): Manual adds one, Import takes a .csv or PDFs.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  FiX,
  FiUserPlus,
  FiUpload,
  FiDownload,
  FiRefreshCw,
  FiFile,
  FiPlus,
  FiTrash2,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { useToast } from "./ToastProvider";
import { fetchWithAuth } from "../utils/authFetch";
import { newBatchId } from "../utils/batchId";
import { parseCsvFile } from "../lib/parseCsvFile";
import { encryptFileForUpload } from "../utils/uploadEncryption";
import { PageScrollLock } from "../utils/useLockPageScroll";

// ── Theme tokens (mirror scoutm8's ink/surface/accent palette onto the
// app's light/dark theme so the modal looks at home in both modes) ──
// Aliases of the app tokens on :root (see theme/ThemeContext); no palette of its own.
const TOKENS = {
  bg: "var(--bg)",
  bg2: "var(--bg-2)",
  surface: "var(--surface)",
  surfaceTint: "var(--surface-tint)",
  ink: "var(--ink)",
  inkSoft: "var(--ink-soft)",
  inkMuted: "var(--ink-muted)",
  line: "var(--line)",
  lineStrong: "var(--line-strong)",
  accent: "var(--accent)",
  accentSoft: "var(--accent-soft)",
  accentInk: "var(--accent-ink)",
  danger: "var(--danger)",
  fontDisplay: "var(--font-display)",
};

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: blur(2px);
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: 40px 24px;
  /* 24px gutters left a phone a ~327px modal. */
  @media (max-width: 640px) {
    padding: 12px;
  }
`;

const Modal = styled.div`
  background: ${TOKENS.surface};
  color: ${TOKENS.ink};
  border: 1px solid ${TOKENS.line};
  border-radius: 16px;
  width: 100%;
  max-width: 540px;
  /* Fixed height so the modal footprint stays constant when switching tabs.
   * The Body scrolls for the tall Manual form; shorter tabs just leave space
   * below. Caps to the viewport on short screens. */
  height: min(640px, calc(100vh - 80px));
  /* dvh so the footer clears a phone's browser toolbar. */
  @media (max-width: 640px) {
    height: min(640px, calc(100dvh - 24px));
  }
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
`;

const Head = styled.div`
  padding: 20px 24px 14px;
  border-bottom: 1px solid ${TOKENS.line};
  @media (max-width: 640px) {
    padding: 16px 16px 12px;
  }
`;

const HeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
`;

const Title = styled.div`
  font-family: ${TOKENS.fontDisplay};
  font-size: 24px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.005em;
`;

const Sub = styled.div`
  font-size: 13px;
  color: ${TOKENS.inkMuted};
  margin-top: 4px;
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: 0;
  color: ${TOKENS.inkSoft};
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s, color 0.12s;
  flex-shrink: 0;
  &:hover {
    background: ${TOKENS.bg2};
    color: ${TOKENS.ink};
  }
  /* Stay square; the global 44px floor stretched it into a tall pill. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const Tabs = styled.div`
  display: inline-flex;
  background: ${TOKENS.bg2};
  border-radius: 999px;
  padding: 3px;
  margin-top: 14px;
`;

const Tab = styled.button`
  height: 30px;
  padding: 0 14px;
  border: 0;
  background: ${({ $active }) =>
    $active ? TOKENS.ink : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--on-fill)" : TOKENS.inkSoft};
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  &:hover {
    color: ${({ $active }) =>
      $active ? "var(--on-fill)" : TOKENS.ink};
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const Body = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  /* min-height: 0 unlocks the flex item so it can actually shrink below
   * its content height — without this, the Body grows to fit and the
   * scrollbar never appears even when content exceeds the modal height. */
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  label {
    font-size: 12px;
    color: ${TOKENS.inkSoft};
    font-weight: 500;
    line-height: 1;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 14px;
  row-gap: 14px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${TOKENS.line};
  background: ${TOKENS.surfaceTint};
  color: ${TOKENS.ink};
  font: inherit;
  font-size: 13.5px;
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
  &::placeholder {
    color: ${TOKENS.inkMuted};
  }
  &:focus {
    background: ${TOKENS.surface};
    border-color: ${TOKENS.accent};
    box-shadow: 0 0 0 3px ${TOKENS.accentSoft};
  }
`;

/* Multiline variant of <Input>. Used for the experience/education
 * description fields where the candidate might paste a paragraph. */
const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 64px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${TOKENS.line};
  background: ${TOKENS.surfaceTint};
  color: ${TOKENS.ink};
  font: inherit;
  font-size: 13.5px;
  outline: none;
  resize: vertical;
  transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
  &:focus {
    background: ${TOKENS.surface};
    border-color: ${TOKENS.accent};
    box-shadow: 0 0 0 3px ${TOKENS.accentSoft};
  }
`;

// Optional resume group (experience, education, certifications); collapsed so the form stays short.
const Collapsible = styled.div`
  border-top: 1px solid ${TOKENS.line};
  &:last-of-type {
    border-bottom: 1px solid ${TOKENS.line};
  }
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 2px;
  background: transparent;
  border: 0;
  color: ${TOKENS.ink};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  &:hover {
    color: ${TOKENS.accent};
  }
  .meta {
    margin-left: auto;
    font-size: 12px;
    font-weight: 400;
    color: ${TOKENS.inkMuted};
  }
`;

const CollapsibleBody = styled.div`
  padding: 2px 0 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// One entry in a resume group, with a remove button top-right.
const EntryCard = styled.div`
  position: relative;
  border-radius: 8px;
  background: ${TOKENS.surfaceTint};
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EntryRemoveBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: ${TOKENS.inkMuted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${TOKENS.surfaceTint};
    color: ${TOKENS.ink};
  }
  /* At 44px tall this overlapped the first input of the entry. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const AddRowBtn = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px dashed ${TOKENS.line};
  color: ${TOKENS.inkSoft};
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12.5px;
  cursor: pointer;
  &:hover {
    color: ${TOKENS.ink};
    border-color: ${TOKENS.accent};
    background: ${TOKENS.accentSoft};
  }
`;

const InlineCheckbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: ${TOKENS.inkSoft};
  cursor: pointer;
  input {
    accent-color: ${TOKENS.accent};
  }
  @media (max-width: 450px) {
    input {
      min-height: 0;
    }
  }
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === "primary" || $variant === "accent"
        ? "transparent"
        : TOKENS.line};
  background: ${({ $variant }) =>
    $variant === "primary" || $variant === "accent"
      ? TOKENS.accent
      : TOKENS.surface};
  color: ${({ $variant }) =>
    $variant === "primary" || $variant === "accent"
      ? "var(--on-fill)"
      : TOKENS.ink};
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  white-space: nowrap;
  justify-content: center;
  &:hover {
    opacity: 0.92;
    border-color: ${({ $variant }) =>
      $variant === "primary" || $variant === "accent"
        ? "transparent"
        : TOKENS.lineStrong};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* Keep 32px like the app's shared Btn; the 44px floor looked chunky. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const Drop = styled.div`
  border: 2px dashed
    ${({ $over }) =>
      $over ? TOKENS.accent : TOKENS.lineStrong};
  border-radius: 12px;
  padding: 28px 20px;
  text-align: center;
  background: ${({ $over }) =>
    $over ? TOKENS.accentSoft : TOKENS.surfaceTint};
  transition: border-color 0.12s, background 0.12s;
`;

const DropIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${TOKENS.bg2};
  color: ${TOKENS.inkSoft};
  display: grid;
  place-items: center;
  margin: 0 auto 12px;
`;

const Foot = styled.div`
  padding: 14px 24px;
  border-top: 1px solid ${TOKENS.line};
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: ${TOKENS.surfaceTint};
  @media (max-width: 640px) {
    padding: 12px 16px;
  }
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--danger-soft);
  color: ${TOKENS.danger};
  font-size: 13px;
`;

const Spin = styled(FiRefreshCw)`
  animation: ${spin} 1s linear infinite;
`;

// CSV column auto-mapping (no UI — match scoutm8's "we'll match automatically")
function autoMap(headers) {
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
  const map = {};
  const guesses = {
    name: ["name", "fullname", "candidate"],
    position: ["position", "title", "role", "jobtitle"],
    company: ["company", "organization", "org", "firm", "employer"],
    email: ["email", "emailaddress", "mail", "probableemail"],
    linkedin: ["linkedin", "linkedinurl", "link", "profile"],
    location: ["location", "city", "country"],
  };
  for (const target of Object.keys(guesses)) {
    const found = headers.find((h) => {
      const n = norm(h);
      return guesses[target].some((c) => n === c || n.includes(c));
    });
    if (found) map[target] = found;
  }
  return map;
}

export default function AddCandidatesModal({ open, onClose, onSaved }) {
  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const t = TOKENS;

  const [tab, setTab] = useState("manual"); // manual | import
  const [form, setForm] = useState({
    name: "",
    position: "",
    company: "",
    location: "",
    country: "",
    email: "",
    linkedin: "",
  });
  // Same field names as the PDF import produces; Outreach reads them directly.
  const [experience, setExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [showExperience, setShowExperience] = useState(false);
  const [showEducation, setShowEducation] = useState(false);
  const [showCertifications, setShowCertifications] = useState(false);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [importStatus, setImportStatus] = useState(""); // for in-flight import feedback
  // PDFs wait here until "Import" is clicked; nothing uploads on drop.
  const [pdfQueue, setPdfQueue] = useState([]);
  const [progress, setProgress] = useState(null); // { done, total } while a batch runs
  const fileInputRef = useRef(null);
  // The PDF run this modal is showing; closing detaches it so it can't touch a reopened modal.
  const pdfRunRef = useRef(0);

  // Reset state when modal closes
  useEffect(() => {
    if (open) return;
    // A run in flight keeps going in the background (Background jobs shows it); the staged files go with it.
    pdfRunRef.current += 1;
    setPdfQueue([]);
    setProgress(null);
    setTab("manual");
    setForm({
      name: "",
      position: "",
      company: "",
      location: "",
      country: "",
      email: "",
      linkedin: "",
    });
    setExperience([]);
    setEducation([]);
    setCertifications([]);
    setShowExperience(false);
    setShowEducation(false);
    setShowCertifications(false);
    setOver(false);
    setBusy(false);
    setError("");
    setImportStatus("");
  }, [open]);

  if (!open) return null;

  const encryptionEnabled =
    process.env.REACT_APP_ENABLE_UPLOAD_ENCRYPTION === "true";

  // ── Save helpers ──────────────────────────────────────────────────
  // `batch` ({ batchId, batchTotal }) makes a multi-chunk import show as one activity entry.
  async function postCandidates(list, batch = null) {
    const CHUNK = 100;
    for (let i = 0; i < list.length; i += CHUNK) {
      const chunk = list.slice(i, i + CHUNK);
      const res = await fetchWithAuth(
        `/api/user-candidates/${encodeURIComponent(user.sub)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ candidates: chunk, ...(batch || {}) }),
        },
      );
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
    }
  }

  // ── Manual submit ─────────────────────────────────────────────────
  // Same candidate shape as the PDF import; field names must not drift.
  async function submitManual() {
    setError("");
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!isAuthenticated || !user?.sub) {
      setError("Please sign in.");
      return;
    }
    setBusy(true);
    try {
      // Drop entries the user added rows for but left entirely empty so
      // the saved candidate doesn't carry phantom blank sections.
      const cleanedExperience = experience
        .map((e) => ({
          title: (e.title || "").trim(),
          company: (e.company || "").trim(),
          startDate: (e.startDate || "").trim(),
          endDate: (e.endDate || "").trim(),
          current: !!e.current,
          description: (e.description || "").trim(),
        }))
        .filter(
          (e) =>
            e.title ||
            e.company ||
            e.startDate ||
            e.endDate ||
            e.description,
        );
      const cleanedEducation = education
        .map((e) => ({
          degree: (e.degree || "").trim(),
          school: (e.school || "").trim(),
          startDate: (e.startDate || "").trim(),
          endDate: (e.endDate || "").trim(),
        }))
        .filter((e) => e.degree || e.school || e.startDate || e.endDate);
      const cleanedCertifications = certifications
        .map((c) => ({
          name: (c.name || "").trim(),
          issuer: (c.issuer || "").trim(),
        }))
        .filter((c) => c.name || c.issuer);

      const candidate = {
        name: form.name.trim(),
        position: form.position.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        probable_email: form.email.trim(),
        link: form.linkedin.trim(),
        thumbnail_src: "",
        location: form.location.trim(),
        country: form.country.trim(),
        education: cleanedEducation,
        certifications: cleanedCertifications,
        experience: cleanedExperience,
        source: "Manual",
      };
      await postCandidates([candidate]);
      showToast && showToast("Candidate added");
      onSaved && onSaved([candidate]);
      onClose();
    } catch (err) {
      setError(err?.message || "Could not save");
    } finally {
      setBusy(false);
    }
  }

  // ── CSV template ──────────────────────────────────────────────────
  // Builds a sample CSV whose headers match autoMap() above so the
  // downloaded file imports cleanly without any column remapping.
  function downloadTemplate() {
    const headers = ["name", "position", "company", "email", "linkedin", "location"];
    const sample = [
      [
        "Eleanor Mae Whitfield",
        "Senior Engineer",
        "Stripe",
        "eleanor@stripe.com",
        "https://linkedin.com/in/eleanor",
        "San Francisco, CA",
      ],
    ];
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...sample].map((row) => row.map(esc).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "candidates-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ── CSV / PDF import ──────────────────────────────────────────────
  async function fetchUploadKey() {
    const res = await fetchWithAuth("/api/me/upload-key");
    if (!res.ok) throw new Error("Could not fetch encryption key");
    const body = await res.json();
    if (!body?.publicKey || !body?.keyArn) throw new Error("Invalid key");
    return body;
  }

  const MAX_PDFS = 50;

  // Entry for the picker and the drop zone: CSV runs at once (one file), PDFs are staged.
  function handleImportFiles(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setError("");
    const isPdf = (f) => f.name.toLowerCase().endsWith(".pdf");
    const pdfs = files.filter(isPdf);
    const others = files.filter((f) => !isPdf(f));
    if (others.length) {
      if (pdfs.length) {
        setError("Import one type at a time: a CSV, or a set of PDFs.");
        return;
      }
      handleImportFile(others[0]);
      return;
    }
    const tooBig = pdfs.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) {
      setError(`${tooBig.name} is too large (max 10MB per PDF).`);
      return;
    }
    setPdfQueue((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const merged = [...prev, ...pdfs.filter((f) => !seen.has(`${f.name}:${f.size}`))];
      if (merged.length > MAX_PDFS) {
        setError(`Up to ${MAX_PDFS} PDFs at once — the first ${MAX_PDFS} are kept.`);
      }
      return merged.slice(0, MAX_PDFS);
    });
  }

  // The backend takes at most 10MB per request and parks each request's files in one
  // queue document, so the staged PDFs go up in size-bounded batches, one after another.
  // The bar counts files the worker has finished across all batches: 1/N … N/N.
  const BATCH_BYTES = 6 * 1024 * 1024;
  const BATCH_FILES = 10;

  function batchPdfs(files) {
    const batches = [];
    let cur = [];
    let bytes = 0;
    for (const f of files) {
      if (cur.length && (bytes + f.size > BATCH_BYTES || cur.length >= BATCH_FILES)) {
        batches.push(cur);
        cur = [];
        bytes = 0;
      }
      cur.push(f);
      bytes += f.size;
    }
    if (cur.length) batches.push(cur);
    return batches;
  }

  async function uploadPdfBatch(files, publicKey, batch) {
    const formData = new FormData();
    // Requests of one staged upload share an id, so Background jobs shows one entry.
    formData.append("batchId", batch.id);
    formData.append("batchTotal", String(batch.total));
    if (publicKey) {
      const manifests = [];
      for (const file of files) {
        const enc = await encryptFileForUpload(file, publicKey);
        formData.append(
          "files[]",
          new File([enc.encryptedBlob], `${file.name}.enc`, {
            type: "application/octet-stream",
          }),
        );
        manifests.push(enc.manifest);
      }
      formData.append("upload_encrypted", "true");
      formData.append("encryption_manifest", JSON.stringify({ files: manifests }));
    } else {
      files.forEach((file) => formData.append("files[]", file));
    }
    formData.append("userId", user.sub);
    const res = await fetchWithAuth("/api/upload-pdf-candidates-page", {
      method: "POST",
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `Upload failed (${res.status})`);
    return data.jobId;
  }

  async function importPdfQueue() {
    const files = pdfQueue;
    if (!files.length || busy) return;
    if (!isAuthenticated || !user?.sub) {
      setError("Please sign in.");
      return;
    }
    setError("");
    setBusy(true);
    const run = ++pdfRunRef.current;
    const live = () => pdfRunRef.current === run;
    const total = files.length;
    setProgress({ done: 0, total });
    setImportStatus(`Uploading 0/${total}…`);
    try {
      const publicKey = encryptionEnabled ? (await fetchUploadKey()).publicKey : null;
      // Every batch is queued first so the worker never waits on the browser; then all
      // the jobs are watched together and the bar counts files finished across them.
      const jobs = [];
      let uploaded = 0;
      const batchTag = {
        id: newBatchId(),
        total,
      };
      for (const batch of batchPdfs(files)) {
        const jobId = await uploadPdfBatch(batch, publicKey, batchTag);
        jobs.push({ jobId, size: batch.length, processed: 0, result: null, settled: false });
        uploaded += batch.length;
        if (live()) setImportStatus(`Uploading ${uploaded}/${total}…`);
      }
      // A few minutes plus half a minute per file overall.
      const deadline = Date.now() + 180000 + total * 30000;
      while (jobs.some((j) => !j.settled) && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1500));
        await Promise.all(
          jobs
            .filter((j) => !j.settled)
            .map(async (j) => {
              const sr = await fetchWithAuth(
                `/api/upload-pdf-candidates-page/status?jobId=${encodeURIComponent(j.jobId)}`,
              );
              const body = await sr.json().catch(() => ({}));
              j.processed = Math.min(j.size, Number(body.processed) || 0);
              if (body.status === "succeeded") {
                j.processed = j.size;
                j.result = body.result || {};
                j.settled = true;
              } else if (body.status === "failed") {
                j.result = { success_count: 0, failed_count: j.size, error: body.error };
                j.settled = true;
              }
            }),
        );
        const done = jobs.reduce((n, j) => n + j.processed, 0);
        if (live()) {
          setProgress({ done, total });
          setImportStatus(`Reading ${done}/${total}…`);
        }
      }
      if (jobs.some((j) => !j.settled)) {
        throw new Error("Processing is taking too long — check back in a minute.");
      }
      const ok = jobs.reduce((n, j) => n + (j.result?.success_count ?? 0), 0);
      const failed = jobs.reduce((n, j) => n + (j.result?.failed_count ?? 0), 0);
      showToast &&
        showToast(
          failed
            ? `Imported ${ok} of ${total} PDFs — ${failed} couldn't be read`
            : `Imported ${ok} candidate${ok === 1 ? "" : "s"} from PDF`,
        );
      onSaved && onSaved();
      if (live()) onClose();
    } catch (err) {
      if (live()) setError(err?.message || "Upload failed");
      else showToast && showToast(err?.message || "PDF import failed");
    } finally {
      if (live()) {
        setBusy(false);
        setImportStatus("");
        setProgress(null);
      }
    }
  }

  async function handleImportFile(file) {
    if (!file) return;
    setError("");
    if (!isAuthenticated || !user?.sub) {
      setError("Please sign in.");
      return;
    }
    const name = file.name.toLowerCase();
    if (name.endsWith(".csv")) {
      setBusy(true);
      setImportStatus("Parsing CSV…");
      try {
        const result = await parseCsvFile(file);
        const headers = result.meta?.fields || [];
        const rows = result.data || [];
        if (!headers.length || !rows.length) {
          throw new Error("CSV is empty or has no header row.");
        }
        const mapping = autoMap(headers);
        if (!mapping.name) {
          throw new Error(
            'Could not find a "Name" column. Rename a column to "Name" and try again.',
          );
        }
        const get = (row, k) => (mapping[k] ? row[mapping[k]] : "");
        const mapped = rows
          .map((row) => ({
            name: String(get(row, "name") || "").trim() || "Candidate",
            position: String(get(row, "position") || "").trim(),
            company: String(get(row, "company") || "").trim(),
            email: String(get(row, "email") || "").trim(),
            probable_email: String(get(row, "email") || "").trim(),
            link: String(get(row, "linkedin") || "").trim(),
            location: String(get(row, "location") || "").trim(),
            source: "csv",
          }))
          .filter((r) => r.name && (r.position || r.company || r.email));
        const limited = mapped.slice(0, 5000);
        if (!limited.length) {
          throw new Error("No usable rows after auto-mapping.");
        }
        setImportStatus(`Saving ${limited.length} candidates…`);
        await postCandidates(limited, { batchId: newBatchId(), batchTotal: limited.length });
        showToast &&
          showToast(
            `Imported ${limited.length} candidate${limited.length === 1 ? "" : "s"}`,
          );
        onSaved && onSaved(limited);
        onClose();
      } catch (err) {
        setError(err?.message || "CSV import failed");
      } finally {
        setBusy(false);
        setImportStatus("");
      }
    } else if (name.endsWith(".pdf")) {
      handleImportFiles([file]);
    } else {
      setError("Only .csv and .pdf files are supported.");
    }
  }

  return createPortal(
    <Backdrop>
      <PageScrollLock />
      <Modal>
        <Head>
          <HeadRow>
            <div>
              <Title>Add a candidate</Title>
              <Sub>Paste details manually, or import a CSV.</Sub>
            </div>
            <IconBtn onClick={onClose} aria-label="Close">
              <FiX size={16} />
            </IconBtn>
          </HeadRow>
          <Tabs>
            <Tab $active={tab === "manual"} onClick={() => setTab("manual")}>
              <FiUserPlus size={12} /> Manual
            </Tab>
            <Tab $active={tab === "import"} onClick={() => setTab("import")}>
              <FiUpload size={12} /> Import CSV
            </Tab>
          </Tabs>
        </Head>

        <Body>
          {tab === "manual" && (
            <>
              <Field>
                <label>Full name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Eleanor Mae Whitfield"
                />
              </Field>
              <FieldRow>
                <Field>
                  <label>Role / title</label>
                  <Input
                    value={form.position}
                    onChange={(e) =>
                      setForm({ ...form, position: e.target.value })
                    }
                    placeholder="Senior Engineer"
                  />
                </Field>
                <Field>
                  <label>Company</label>
                  <Input
                    value={form.company}
                    onChange={(e) =>
                      setForm({ ...form, company: e.target.value })
                    }
                    placeholder="Stripe"
                  />
                </Field>
              </FieldRow>
              <FieldRow>
                <Field>
                  <label>Location</label>
                  <Input
                    value={form.location}
                    onChange={(e) =>
                      setForm({ ...form, location: e.target.value })
                    }
                    placeholder="San Francisco, CA"
                  />
                </Field>
                <Field>
                  <label>Country</label>
                  <Input
                    value={form.country}
                    onChange={(e) =>
                      setForm({ ...form, country: e.target.value })
                    }
                    placeholder="USA"
                  />
                </Field>
              </FieldRow>
              <FieldRow>
                <Field>
                  <label>Email</label>
                  <Input
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    placeholder="name@company.com"
                  />
                </Field>
                <Field>
                  <label>LinkedIn URL</label>
                  <Input
                    value={form.linkedin}
                    onChange={(e) =>
                      setForm({ ...form, linkedin: e.target.value })
                    }
                    placeholder="linkedin.com/in/…"
                  />
                </Field>
              </FieldRow>

              {/* ── Experience (optional) ── */}
              <Collapsible>
                <CollapsibleHeader
                  type="button"
                  onClick={() => setShowExperience((v) => !v)}
                >
                  {showExperience ? (
                    <FiChevronDown size={14} />
                  ) : (
                    <FiChevronRight size={14} />
                  )}
                  Experience
                  <span className="meta">
                    {experience.length > 0
                      ? `${experience.length} entr${
                          experience.length === 1 ? "y" : "ies"
                        }`
                      : "Optional"}
                  </span>
                </CollapsibleHeader>
                {showExperience && (
                  <CollapsibleBody>
                    {experience.map((entry, idx) => (
                      <EntryCard key={idx}>
                        <EntryRemoveBtn
                          type="button"
                          aria-label="Remove this experience entry"
                          onClick={() =>
                            setExperience((arr) =>
                              arr.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <FiTrash2 size={12} />
                        </EntryRemoveBtn>
                        <FieldRow>
                          <Field>
                            <label>Title</label>
                            <Input
                              value={entry.title || ""}
                              onChange={(e) =>
                                setExperience((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, title: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="Senior Engineer"
                            />
                          </Field>
                          <Field>
                            <label>Company</label>
                            <Input
                              value={entry.company || ""}
                              onChange={(e) =>
                                setExperience((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, company: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="Stripe"
                            />
                          </Field>
                        </FieldRow>
                        <FieldRow>
                          <Field>
                            <label>Start (e.g. 2022-03)</label>
                            <Input
                              value={entry.startDate || ""}
                              onChange={(e) =>
                                setExperience((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, startDate: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="2022-03"
                            />
                          </Field>
                          <Field>
                            <label>End</label>
                            <Input
                              value={entry.endDate || ""}
                              onChange={(e) =>
                                setExperience((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, endDate: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="2024-08"
                              disabled={!!entry.current}
                            />
                          </Field>
                        </FieldRow>
                        <InlineCheckbox>
                          <input
                            type="checkbox"
                            checked={!!entry.current}
                            onChange={(e) =>
                              setExperience((arr) =>
                                arr.map((it, i) =>
                                  i === idx
                                    ? {
                                        ...it,
                                        current: e.target.checked,
                                        ...(e.target.checked
                                          ? { endDate: "" }
                                          : {}),
                                      }
                                    : it,
                                ),
                              )
                            }
                          />
                          Currently working here
                        </InlineCheckbox>
                        <Field>
                          <label>Description</label>
                          <Textarea
                            value={entry.description || ""}
                            onChange={(e) =>
                              setExperience((arr) =>
                                arr.map((it, i) =>
                                  i === idx
                                    ? { ...it, description: e.target.value }
                                    : it,
                                ),
                              )
                            }
                            placeholder="What did they work on?"
                          />
                        </Field>
                      </EntryCard>
                    ))}
                    <AddRowBtn
                      type="button"
                      onClick={() =>
                        setExperience((arr) => [
                          ...arr,
                          {
                            title: "",
                            company: "",
                            startDate: "",
                            endDate: "",
                            current: false,
                            description: "",
                          },
                        ])
                      }
                    >
                      <FiPlus size={12} /> Add experience
                    </AddRowBtn>
                  </CollapsibleBody>
                )}
              </Collapsible>

              {/* ── Education (optional) ── */}
              <Collapsible>
                <CollapsibleHeader
                  type="button"
                  onClick={() => setShowEducation((v) => !v)}
                >
                  {showEducation ? (
                    <FiChevronDown size={14} />
                  ) : (
                    <FiChevronRight size={14} />
                  )}
                  Education
                  <span className="meta">
                    {education.length > 0
                      ? `${education.length} entr${
                          education.length === 1 ? "y" : "ies"
                        }`
                      : "Optional"}
                  </span>
                </CollapsibleHeader>
                {showEducation && (
                  <CollapsibleBody>
                    {education.map((entry, idx) => (
                      <EntryCard key={idx}>
                        <EntryRemoveBtn
                          type="button"
                          aria-label="Remove this education entry"
                          onClick={() =>
                            setEducation((arr) =>
                              arr.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <FiTrash2 size={12} />
                        </EntryRemoveBtn>
                        <FieldRow>
                          <Field>
                            <label>Degree</label>
                            <Input
                              value={entry.degree || ""}
                              onChange={(e) =>
                                setEducation((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, degree: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="BS Computer Science"
                            />
                          </Field>
                          <Field>
                            <label>School</label>
                            <Input
                              value={entry.school || ""}
                              onChange={(e) =>
                                setEducation((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, school: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="UC Berkeley"
                            />
                          </Field>
                        </FieldRow>
                        <FieldRow>
                          <Field>
                            <label>Start</label>
                            <Input
                              value={entry.startDate || ""}
                              onChange={(e) =>
                                setEducation((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, startDate: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="2014"
                            />
                          </Field>
                          <Field>
                            <label>End</label>
                            <Input
                              value={entry.endDate || ""}
                              onChange={(e) =>
                                setEducation((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, endDate: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="2018"
                            />
                          </Field>
                        </FieldRow>
                      </EntryCard>
                    ))}
                    <AddRowBtn
                      type="button"
                      onClick={() =>
                        setEducation((arr) => [
                          ...arr,
                          {
                            degree: "",
                            school: "",
                            startDate: "",
                            endDate: "",
                          },
                        ])
                      }
                    >
                      <FiPlus size={12} /> Add education
                    </AddRowBtn>
                  </CollapsibleBody>
                )}
              </Collapsible>

              {/* ── Certifications (optional) ── */}
              <Collapsible>
                <CollapsibleHeader
                  type="button"
                  onClick={() => setShowCertifications((v) => !v)}
                >
                  {showCertifications ? (
                    <FiChevronDown size={14} />
                  ) : (
                    <FiChevronRight size={14} />
                  )}
                  Certifications
                  <span className="meta">
                    {certifications.length > 0
                      ? `${certifications.length} entr${
                          certifications.length === 1 ? "y" : "ies"
                        }`
                      : "Optional"}
                  </span>
                </CollapsibleHeader>
                {showCertifications && (
                  <CollapsibleBody>
                    {certifications.map((entry, idx) => (
                      <EntryCard key={idx}>
                        <EntryRemoveBtn
                          type="button"
                          aria-label="Remove this certification"
                          onClick={() =>
                            setCertifications((arr) =>
                              arr.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <FiTrash2 size={12} />
                        </EntryRemoveBtn>
                        <FieldRow>
                          <Field>
                            <label>Name</label>
                            <Input
                              value={entry.name || ""}
                              onChange={(e) =>
                                setCertifications((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, name: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="AWS Solutions Architect"
                            />
                          </Field>
                          <Field>
                            <label>Issuer</label>
                            <Input
                              value={entry.issuer || ""}
                              onChange={(e) =>
                                setCertifications((arr) =>
                                  arr.map((it, i) =>
                                    i === idx
                                      ? { ...it, issuer: e.target.value }
                                      : it,
                                  ),
                                )
                              }
                              placeholder="Amazon Web Services"
                            />
                          </Field>
                        </FieldRow>
                      </EntryCard>
                    ))}
                    <AddRowBtn
                      type="button"
                      onClick={() =>
                        setCertifications((arr) => [
                          ...arr,
                          { name: "", issuer: "" },
                        ])
                      }
                    >
                      <FiPlus size={12} /> Add certification
                    </AddRowBtn>
                  </CollapsibleBody>
                )}
              </Collapsible>

              {error && <ErrorBox>{error}</ErrorBox>}
            </>
          )}

          {tab === "import" && (
            <>
              <Drop
                $over={over}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  handleImportFiles(e.dataTransfer.files);
                }}
              >
                <DropIcon>
                  {busy ? <Spin size={20} /> : <FiUpload size={20} />}
                </DropIcon>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {busy
                    ? importStatus || "Working…"
                    : pdfQueue.length
                      ? `${pdfQueue.length} PDF${pdfQueue.length === 1 ? "" : "s"} ready`
                      : "Drop a CSV, or up to 50 PDFs"}
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: t.inkMuted,
                    margin: "4px 0 14px",
                  }}
                >
                  {busy
                    ? "Please wait."
                    : pdfQueue.length
                      ? "Nothing is read until you click Import. Drop more to add them."
                      : "CSV columns are matched automatically (up to 5,000 rows). PDFs up to 10MB each."}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.pdf,application/pdf,text/csv"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleImportFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <Btn
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                >
                  {pdfQueue.length ? "Add more" : "Browse files"}
                </Btn>
                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadTemplate();
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: t.accent || t.ink,
                      fontSize: 12,
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <FiDownload size={12} /> Download CSV template
                  </button>
                </div>
              </Drop>
              {progress && (
                <div style={{ marginTop: 12 }} aria-live="polite">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12.5,
                      color: t.inkMuted,
                      marginBottom: 6,
                    }}
                  >
                    <span>Reading PDFs</span>
                    <b style={{ color: t.ink }}>
                      {progress.done}/{progress.total}
                    </b>
                  </div>
                  <div
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={progress.total}
                    aria-valuenow={progress.done}
                    style={{
                      height: 6,
                      borderRadius: 999,
                      background: t.border || "rgba(0,0,0,0.08)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                        background: t.accent || t.ink,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              )}
              {pdfQueue.length > 0 && !busy && (
                <div
                  style={{
                    marginTop: 12,
                    maxHeight: 220,
                    overflowY: "auto",
                    border: `1px solid ${t.border || "rgba(0,0,0,0.08)"}`,
                    borderRadius: 10,
                  }}
                >
                  {pdfQueue.map((f, i) => (
                    <div
                      key={`${f.name}:${f.size}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 12px",
                        fontSize: 13,
                        borderTop: i ? `1px solid ${t.border || "rgba(0,0,0,0.06)"}` : "none",
                      }}
                    >
                      <span style={{ color: t.inkMuted, width: 22, textAlign: "right" }}>{i + 1}.</span>
                      <FiFile size={13} style={{ flexShrink: 0 }} />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={f.name}
                      >
                        {f.name}
                      </span>
                      <span style={{ color: t.inkMuted, fontSize: 12 }}>
                        {(f.size / 1024).toFixed(0)} KB
                      </span>
                      <IconBtn
                        aria-label={`Remove ${f.name}`}
                        onClick={() =>
                          setPdfQueue((prev) => prev.filter((x) => x !== f))
                        }
                      >
                        <FiX size={13} />
                      </IconBtn>
                    </div>
                  ))}
                </div>
              )}
              {error && <ErrorBox>{error}</ErrorBox>}
            </>
          )}
        </Body>

        <Foot>
          <Btn onClick={onClose} disabled={busy}>
            Cancel
          </Btn>
          {tab === "import" && pdfQueue.length > 0 && (
            <Btn $variant="primary" onClick={importPdfQueue} disabled={busy}>
              {busy ? (
                <>
                  <Spin size={13} />{" "}
                  {progress ? `${progress.done}/${progress.total}` : "Uploading…"}
                </>
              ) : (
                <>
                  <FiUpload size={12} /> Import {pdfQueue.length} PDF
                  {pdfQueue.length === 1 ? "" : "s"}
                </>
              )}
            </Btn>
          )}
          {tab === "manual" && (
            <Btn
              $variant="primary"
              onClick={submitManual}
              disabled={!form.name.trim() || busy}
            >
              {busy ? (
                <>
                  <Spin size={13} /> Saving…
                </>
              ) : (
                <>
                  <FiFile size={12} /> Add candidate
                </>
              )}
            </Btn>
          )}
        </Foot>
      </Modal>
    </Backdrop>,
    document.body,
  );
}
